import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Building2,
  Calendar,
  Clock,
  Package,
  Users,
  Bot,
  FileEdit,
  Bell,
  Shield,
  BarChart3,
  Plus,
  Trash2,
  Save,
  Mail,
  Phone,
  Loader2,
  Info,
  UserPlus,
  MoreVertical,
  UserCog,
  UserX,
  RefreshCw,
  Check,
  X,
} from "lucide-react";

// Info tooltip component for settings
const InfoTooltip = ({ text }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="h-4 w-4 text-muted-foreground hover:text-primary cursor-help ml-1.5 inline-block" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs bg-popover text-popover-foreground border shadow-lg">
        <p className="text-sm">{text}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Label with optional info tooltip
const LabelWithInfo = ({ children, info }) => (
  <div className="flex items-center">
    <Label>{children}</Label>
    {info && <InfoTooltip text={info} />}
  </div>
);

const SettingsPage = () => {
  const { user, tenant } = useAuth();
  const [settings, setSettings] = useState(null);
  const [orgUnits, setOrgUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("organization");
  
  // User Management State
  const [roles, setRoles] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [removeUserDialog, setRemoveUserDialog] = useState({ open: false, user: null });
  const [inviteForm, setInviteForm] = useState({
    email: "",
    full_name: "",
    role: "member",
  });
  
  // Dialogs
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isOrgUnitDialogOpen, setIsOrgUnitDialogOpen] = useState(false);
  
  // Forms
  const [userForm, setUserForm] = useState({
    email: "",
    full_name: "",
    password: "",
    phone: "",
    role: "viewer",
  });
  
  const [orgUnitForm, setOrgUnitForm] = useState({
    name: "",
    type: "division",
    parent_org_unit_id: null,
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [settingsRes, orgUnitsRes, usersRes, calendarsRes, rolesRes, invitationsRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/settings/org-units`),
        axios.get(`${API}/settings/users`),
        axios.get(`${API}/calendars`),
        axios.get(`${API}/settings/users/roles`).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/users/invitations`).catch(() => ({ data: [] })),
      ]);
      setSettings(settingsRes.data);
      setOrgUnits(orgUnitsRes.data);
      setUsers(usersRes.data);
      setCalendars(calendarsRes.data);
      setRoles(rolesRes.data);
      setInvitations(invitationsRes.data);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  // User Management Functions
  const handleInviteUser = async () => {
    if (!inviteForm.email) {
      toast.error("Email is required");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/settings/users/invite`, inviteForm);
      if (data.invitation_url) {
        // Email delivery is off: the link only exists here. Hand it to the
        // admin instead of claiming an email went out.
        try {
          await navigator.clipboard.writeText(data.invitation_url);
          toast.success(
            `Invitation link for ${inviteForm.email} copied to clipboard — email delivery is off, send it to them yourself.`,
            { duration: 12000 }
          );
        } catch {
          toast.info(`Email delivery is off. Share this link with ${inviteForm.email}: ${data.invitation_url}`, {
            duration: 30000,
          });
        }
      } else if (data.email_sent) {
        toast.success(`Invitation sent to ${inviteForm.email}`);
      } else {
        toast.warning(`Invitation created for ${inviteForm.email}, but the email may not have been delivered.`);
      }
      setIsInviteDialogOpen(false);
      setInviteForm({ email: "", full_name: "", role: "member" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    try {
      await axios.put(`${API}/settings/users/${editingUser.id}`, {
        role: editingUser.role,
        status: editingUser.status,
        full_name: editingUser.full_name,
        phone: editingUser.phone,
      });
      toast.success("User updated");
      setIsEditUserDialogOpen(false);
      setEditingUser(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const handleRemoveUser = async () => {
    if (!removeUserDialog.user) return;
    try {
      await axios.delete(`${API}/settings/users/${removeUserDialog.user.id}`);
      toast.success("User removed");
      setRemoveUserDialog({ open: false, user: null });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove user");
    }
  };

  const handleReactivateUser = async (userId) => {
    try {
      await axios.post(`${API}/settings/users/${userId}/reactivate`);
      toast.success("User reactivated");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate user");
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await axios.delete(`${API}/settings/users/invitations/${invitationId}`);
      toast.success("Invitation cancelled");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel invitation");
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      owner: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      admin: "bg-red-500/20 text-red-400 border-red-500/30",
      project_manager: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      member: "bg-green-500/20 text-green-400 border-green-500/30",
      subcontractor: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    };
    return colors[role] || "bg-muted text-muted-foreground";
  };

  const getRoleDisplayName = (role) => {
    const names = {
      owner: "Owner",
      admin: "Admin",
      project_manager: "Project Manager",
      member: "Member",
      subcontractor: "Subcontractor",
    };
    return names[role] || role;
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/settings`, settings);
      setSettings(response.data);
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateNestedSetting = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const updateDeepNestedSetting = (section, subsection, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [key]: value
        }
      }
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/users`, userForm);
      setUsers([response.data, ...users]);
      setIsUserDialogOpen(false);
      setUserForm({ email: "", full_name: "", password: "", phone: "", role: "viewer" });
      toast.success("User created successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleCreateOrgUnit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/settings/org-units`, orgUnitForm);
      setOrgUnits([...orgUnits, response.data]);
      setIsOrgUnitDialogOpen(false);
      setOrgUnitForm({ name: "", type: "division", parent_org_unit_id: null });
      toast.success("Organization unit created");
    } catch (error) {
      toast.error("Failed to create organization unit");
    }
  };

  const handleDeleteOrgUnit = async (id) => {
    if (!window.confirm("Delete this organization unit?")) return;
    try {
      await axios.delete(`${API}/settings/org-units/${id}`);
      setOrgUnits(orgUnits.filter(o => o.id !== id));
      toast.success("Organization unit deleted");
    } catch (error) {
      toast.error("Failed to delete organization unit");
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: "bg-destructive/20 text-destructive",
      pm: "bg-primary/20 text-primary",
      superintendent: "bg-secondary/20 text-secondary",
      estimator: "bg-amber-500/20 text-amber-400",
      subcontractor: "bg-emerald-500/20 text-emerald-400",
      viewer: "bg-muted text-muted-foreground",
    };
    return (
      <span className={`status-badge capitalize ${roleConfig[role] || roleConfig.viewer}`}>
        {role}
      </span>
    );
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary text-xl">Loading settings...</div>
      </div>
    );
  }

  const tabItems = [
    { value: "organization", label: "Organization", icon: Building2 },
    { value: "users", label: "Users", icon: UserCog },
    { value: "calendars", label: "Calendars", icon: Calendar },
    { value: "scheduling", label: "Scheduling", icon: Clock },
    { value: "procurement", label: "Procurement", icon: Package },
    { value: "workforce", label: "Workforce", icon: Users },
    { value: "automation", label: "AI & Automation", icon: Bot },
    { value: "changes", label: "Change Control", icon: FileEdit },
    { value: "notifications", label: "Notifications", icon: Bell },
    { value: "access", label: "Access & Visibility", icon: Shield },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-1">
            Configure organization-wide settings for {tenant?.name}
          </p>
        </div>
        <Button onClick={handleSaveSettings} disabled={saving} className="bg-primary hover:bg-primary/90">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save Settings
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/50 p-1">
          {tabItems.map((tab) => (
            <TabsTrigger 
              key={tab.value} 
              value={tab.value}
              className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <tab.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Organization Settings */}
        <TabsContent value="organization" className="space-y-6">
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Organization Structure
              </CardTitle>
              <CardDescription>Configure your company's organizational hierarchy</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <LabelWithInfo info="The top-level parent company name that owns all divisions and operating units">
                    Parent Organization Name
                  </LabelWithInfo>
                  <Input
                    value={settings.org_structure.parent_org_name}
                    onChange={(e) => updateNestedSetting("org_structure", "parent_org_name", e.target.value)}
                    placeholder="Parent Company Name"
                    className="bg-muted/50"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <LabelWithInfo info="Divisions are major business segments within your organization (e.g., Commercial, Residential, Industrial)">
                      Enable Divisions
                    </LabelWithInfo>
                    <p className="text-sm text-muted-foreground">Allow division-level organization units</p>
                  </div>
                  <Switch
                    checked={settings.org_structure.divisions_enabled}
                    onCheckedChange={(v) => updateNestedSetting("org_structure", "divisions_enabled", v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <LabelWithInfo info="Operating units are smaller teams or regional offices within a division (e.g., North Region, Special Projects Team)">
                      Enable Operating Units
                    </LabelWithInfo>
                    <p className="text-sm text-muted-foreground">Allow operating unit subdivisions</p>
                  </div>
                  <Switch
                    checked={settings.org_structure.operating_units_enabled}
                    onCheckedChange={(v) => updateNestedSetting("org_structure", "operating_units_enabled", v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <LabelWithInfo info="When enabled, every project must be linked to a specific org unit for proper reporting and cost allocation">
                      Require Project-Org Mapping
                    </LabelWithInfo>
                    <p className="text-sm text-muted-foreground">Projects must be assigned to an org unit</p>
                  </div>
                  <Switch
                    checked={settings.org_structure.project_org_mapping_required}
                    onCheckedChange={(v) => updateNestedSetting("org_structure", "project_org_mapping_required", v)}
                  />
                </div>
              </div>

              <Separator />

              {/* Organization Units List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base">Organization Units</Label>
                  <Dialog open={isOrgUnitDialogOpen} onOpenChange={setIsOrgUnitDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-primary">
                        <Plus className="mr-2 h-4 w-4" />
                        Add Unit
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card">
                      <DialogHeader>
                        <DialogTitle>Add Organization Unit</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateOrgUnit} className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>Name *</Label>
                          <Input
                            value={orgUnitForm.name}
                            onChange={(e) => setOrgUnitForm({ ...orgUnitForm, name: e.target.value })}
                            required
                            className="bg-muted/50"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select value={orgUnitForm.type} onValueChange={(v) => setOrgUnitForm({ ...orgUnitForm, type: v })}>
                            <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="company">Company</SelectItem>
                              <SelectItem value="division">Division</SelectItem>
                              <SelectItem value="operating_unit">Operating Unit</SelectItem>
                              <SelectItem value="department">Department</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-end gap-3">
                          <Button type="button" variant="outline" onClick={() => setIsOrgUnitDialogOpen(false)}>Cancel</Button>
                          <Button type="submit" className="bg-primary">Create</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="space-y-2">
                  {orgUnits.map((unit) => (
                    <div key={unit.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <p className="font-medium">{unit.name}</p>
                        <p className="text-sm text-muted-foreground capitalize">{unit.type}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteOrgUnit(unit.id)} className="text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {orgUnits.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">No organization units configured</p>
                  )}
                </div>
              </div>

              <Separator />

              {/* Team Members */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base">Team Members</Label>
                  <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-primary">
                        <Plus className="mr-2 h-4 w-4" />
                        Add User
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card">
                      <DialogHeader>
                        <DialogTitle>Add Team Member</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>Full Name *</Label>
                          <Input value={userForm.full_name} onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })} required className="bg-muted/50" />
                        </div>
                        <div className="space-y-2">
                          <Label>Email *</Label>
                          <Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} required className="bg-muted/50" />
                        </div>
                        <div className="space-y-2">
                          <Label>Password *</Label>
                          <Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} required minLength={6} className="bg-muted/50" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Phone</Label>
                            <Input value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} className="bg-muted/50" />
                          </div>
                          <div className="space-y-2">
                            <Label>Role</Label>
                            <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                              <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="pm">Project Manager</SelectItem>
                                <SelectItem value="superintendent">Superintendent</SelectItem>
                                <SelectItem value="estimator">Estimator</SelectItem>
                                <SelectItem value="subcontractor">Subcontractor</SelectItem>
                                <SelectItem value="viewer">Viewer</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="flex justify-end gap-3">
                          <Button type="button" variant="outline" onClick={() => setIsUserDialogOpen(false)}>Cancel</Button>
                          <Button type="submit" className="bg-primary">Add User</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="space-y-2">
                  {users.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-semibold">{member.full_name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?"}</span>
                        </div>
                        <div>
                          <p className="font-medium">{member.full_name}</p>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1"><Mail size={12} />{member.email}</span>
                            {member.phone && <span className="flex items-center gap-1"><Phone size={12} />{member.phone}</span>}
                          </div>
                        </div>
                      </div>
                      {getRoleBadge(member.role)}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Management Tab */}
        <TabsContent value="users" className="space-y-6">
          <Card className="stat-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <UserCog className="h-5 w-5 text-primary" />
                    Team Members
                  </CardTitle>
                  <CardDescription>Manage users and their roles in your organization</CardDescription>
                </div>
                <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary hover:bg-primary/90">
                      <UserPlus className="mr-2 h-4 w-4" />
                      Invite User
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card">
                    <DialogHeader>
                      <DialogTitle>Invite Team Member</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Email Address *</Label>
                        <Input
                          type="email"
                          value={inviteForm.email}
                          onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                          placeholder="colleague@company.com"
                          className="bg-muted/50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Full Name (Optional)</Label>
                        <Input
                          value={inviteForm.full_name}
                          onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                          placeholder="John Smith"
                          className="bg-muted/50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Role</Label>
                        <Select
                          value={inviteForm.role}
                          onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
                        >
                          <SelectTrigger className="bg-muted/50">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="project_manager">Project Manager</SelectItem>
                            <SelectItem value="member">Member</SelectItem>
                            <SelectItem value="subcontractor">Subcontractor</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          {inviteForm.role === "admin" && "Full access except ownership transfer"}
                          {inviteForm.role === "project_manager" && "Can manage projects, schedules, and assignments"}
                          {inviteForm.role === "member" && "Can view and contribute to assigned projects"}
                          {inviteForm.role === "subcontractor" && "Limited access to assigned projects only"}
                        </p>
                      </div>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button variant="outline" onClick={() => setIsInviteDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleInviteUser} className="bg-primary">
                          Send Invitation
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Active Users */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Active Users ({users.filter(u => u.status === "active").length})
                </h4>
                <div className="space-y-2">
                  {users.filter(u => u.status === "active").map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold">
                          {u.full_name?.[0]?.toUpperCase() || u.email[0].toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{u.full_name || "No Name"}</p>
                            <Badge variant="outline" className={getRoleBadgeColor(u.role)}>
                              {getRoleDisplayName(u.role)}
                            </Badge>
                            {u.id === user?.id && (
                              <Badge variant="outline" className="text-xs">You</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {u.email}
                            </span>
                            {u.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {u.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!u.email_verified && (
                          <Badge variant="outline" className="text-amber-400 border-amber-500/30">
                            Unverified
                          </Badge>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setEditingUser({ ...u });
                                setIsEditUserDialogOpen(true);
                              }}
                            >
                              <UserCog className="mr-2 h-4 w-4" />
                              Edit User
                            </DropdownMenuItem>
                            {u.id !== user?.id && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => setRemoveUserDialog({ open: true, user: u })}
                                >
                                  <UserX className="mr-2 h-4 w-4" />
                                  Remove User
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pending Invitations */}
              {invitations.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    Pending Invitations ({invitations.length})
                  </h4>
                  <div className="space-y-2">
                    {invitations.map((inv) => (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400">
                            <Mail className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-medium">{inv.email}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Badge variant="outline" className={getRoleBadgeColor(inv.role)}>
                                {getRoleDisplayName(inv.role)}
                              </Badge>
                              <span>•</span>
                              <span>Expires {new Date(inv.expires_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleCancelInvitation(inv.id)}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deactivated Users */}
              {users.filter(u => u.status !== "active").length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    Deactivated Users ({users.filter(u => u.status !== "active").length})
                  </h4>
                  <div className="space-y-2">
                    {users.filter(u => u.status !== "active").map((u) => (
                      <div
                        key={u.id}
                        className="flex items-center justify-between p-4 bg-muted/20 rounded-lg opacity-60"
                      >
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                            {u.full_name?.[0]?.toUpperCase() || u.email[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{u.full_name || u.email}</p>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReactivateUser(u.id)}
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Reactivate
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Role Permissions Matrix */}
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Role Permissions
              </CardTitle>
              <CardDescription>Overview of what each role can do</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Permission</th>
                      <th className="text-center py-3 px-2 font-medium text-amber-400">Owner</th>
                      <th className="text-center py-3 px-2 font-medium text-red-400">Admin</th>
                      <th className="text-center py-3 px-2 font-medium text-blue-400">PM</th>
                      <th className="text-center py-3 px-2 font-medium text-green-400">Member</th>
                      <th className="text-center py-3 px-2 font-medium text-purple-400">Subcontractor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: "View Settings", owner: true, admin: true, pm: false, member: false, sub: false },
                      { name: "Edit Settings", owner: true, admin: true, pm: false, member: false, sub: false },
                      { name: "Manage Users", owner: true, admin: true, pm: false, member: false, sub: false },
                      { name: "Create Projects", owner: true, admin: true, pm: true, member: false, sub: false },
                      { name: "Edit Projects", owner: true, admin: true, pm: true, member: false, sub: false },
                      { name: "Delete Projects", owner: true, admin: true, pm: false, member: false, sub: false },
                      { name: "View All Projects", owner: true, admin: true, pm: true, member: false, sub: false },
                      { name: "View Assigned Projects", owner: true, admin: true, pm: true, member: true, sub: true },
                      { name: "Edit Schedule", owner: true, admin: true, pm: true, member: false, sub: false },
                      { name: "Upload Documents", owner: true, admin: true, pm: true, member: true, sub: false },
                      { name: "Approve Requests", owner: true, admin: true, pm: true, member: false, sub: false },
                      { name: "View Costs", owner: true, admin: true, pm: true, member: false, sub: false },
                      { name: "Use AI Features", owner: true, admin: true, pm: true, member: false, sub: false },
                    ].map((perm, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/20">
                        <td className="py-3 px-4">{perm.name}</td>
                        <td className="text-center py-3 px-2">
                          {perm.owner ? <Check className="h-4 w-4 text-amber-400 mx-auto" /> : <X className="h-4 w-4 text-muted-foreground/30 mx-auto" />}
                        </td>
                        <td className="text-center py-3 px-2">
                          {perm.admin ? <Check className="h-4 w-4 text-red-400 mx-auto" /> : <X className="h-4 w-4 text-muted-foreground/30 mx-auto" />}
                        </td>
                        <td className="text-center py-3 px-2">
                          {perm.pm ? <Check className="h-4 w-4 text-blue-400 mx-auto" /> : <X className="h-4 w-4 text-muted-foreground/30 mx-auto" />}
                        </td>
                        <td className="text-center py-3 px-2">
                          {perm.member ? <Check className="h-4 w-4 text-green-400 mx-auto" /> : <X className="h-4 w-4 text-muted-foreground/30 mx-auto" />}
                        </td>
                        <td className="text-center py-3 px-2">
                          {perm.sub ? <Check className="h-4 w-4 text-purple-400 mx-auto" /> : <X className="h-4 w-4 text-muted-foreground/30 mx-auto" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Edit User Dialog */}
        <Dialog open={isEditUserDialogOpen} onOpenChange={setIsEditUserDialogOpen}>
          <DialogContent className="bg-card">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            {editingUser && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    value={editingUser.full_name || ""}
                    onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                    className="bg-muted/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={editingUser.phone || ""}
                    onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                    className="bg-muted/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select
                    value={editingUser.role}
                    onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                  >
                    <SelectTrigger className="bg-muted/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="project_manager">Project Manager</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="subcontractor">Subcontractor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={editingUser.status}
                    onValueChange={(value) => setEditingUser({ ...editingUser, status: value })}
                  >
                    <SelectTrigger className="bg-muted/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setIsEditUserDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateUser} className="bg-primary">
                    Save Changes
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Remove User Confirmation */}
        <AlertDialog open={removeUserDialog.open} onOpenChange={(open) => setRemoveUserDialog({ ...removeUserDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove User</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove {removeUserDialog.user?.full_name || removeUserDialog.user?.email} from your organization? 
                They will lose access to all projects and data.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleRemoveUser} className="bg-destructive text-destructive-foreground">
                Remove User
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Calendars & Working Rules */}
        <TabsContent value="calendars" className="space-y-6">
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Calendar & Working Rules
              </CardTitle>
              <CardDescription>Configure default working hours and calendar settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <LabelWithInfo info="Standard start time for work activities. Used for scheduling calculations unless overridden at project level.">
                    Default Work Hours Start
                  </LabelWithInfo>
                  <Input
                    type="time"
                    value={settings.calendar_working_rules.default_work_hours_start}
                    onChange={(e) => updateNestedSetting("calendar_working_rules", "default_work_hours_start", e.target.value)}
                    className="bg-muted/50"
                  />
                </div>
                <div className="space-y-2">
                  <LabelWithInfo info="Standard end time for work activities. Affects duration calculations for scheduling.">
                    Default Work Hours End
                  </LabelWithInfo>
                  <Input
                    type="time"
                    value={settings.calendar_working_rules.default_work_hours_end}
                    onChange={(e) => updateNestedSetting("calendar_working_rules", "default_work_hours_end", e.target.value)}
                    className="bg-muted/50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <LabelWithInfo info="The master calendar for your organization that defines holidays, shutdown periods, and non-working days.">
                  Organization Calendar
                </LabelWithInfo>
                <Select 
                  value={settings.calendar_working_rules.org_calendar_id || ""} 
                  onValueChange={(v) => updateNestedSetting("calendar_working_rules", "org_calendar_id", v || null)}
                >
                  <SelectTrigger className="bg-muted/50"><SelectValue placeholder="Select org calendar" /></SelectTrigger>
                  <SelectContent>
                    {calendars.map(cal => (
                      <SelectItem key={cal.id} value={cal.id}>{cal.name} {cal.is_default && "(Default)"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">Manage calendars in the Calendars section</p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <LabelWithInfo info="When enabled, individual projects can have their own calendars with different working hours or holidays that override the org-level defaults.">
                    Allow Project Calendar Overrides
                  </LabelWithInfo>
                  <p className="text-sm text-muted-foreground">Projects can define their own calendars</p>
                </div>
                <Switch
                  checked={settings.calendar_working_rules.project_calendar_override_enabled}
                  onCheckedChange={(v) => updateNestedSetting("calendar_working_rules", "project_calendar_override_enabled", v)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scheduling Behavior */}
        <TabsContent value="scheduling" className="space-y-6">
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Scheduling Behavior
              </CardTitle>
              <CardDescription>Configure how schedules are managed and optimized</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <LabelWithInfo info="When a task duration changes or a dependency shifts, the system will automatically adjust downstream task dates to maintain logical sequence.">
                      Auto Resequencing
                    </LabelWithInfo>
                    <p className="text-sm text-muted-foreground">Automatically resequence tasks when changes occur</p>
                  </div>
                  <Switch
                    checked={settings.scheduling_behavior.auto_resequencing_enabled}
                    onCheckedChange={(v) => updateNestedSetting("scheduling_behavior", "auto_resequencing_enabled", v)}
                  />
                </div>

                <div className="space-y-2">
                  <LabelWithInfo info="Conservative: Minimizes schedule disruption, keeps most dates stable. Moderate: Balances efficiency with stability. Aggressive: Maximizes resource utilization and compresses schedule where possible.">
                    Optimization Aggressiveness
                  </LabelWithInfo>
                  <Select 
                    value={settings.scheduling_behavior.optimization_aggressiveness} 
                    onValueChange={(v) => updateNestedSetting("scheduling_behavior", "optimization_aggressiveness", v)}
                  >
                    <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conservative">Conservative - Minimal changes</SelectItem>
                      <SelectItem value="moderate">Moderate - Balanced approach</SelectItem>
                      <SelectItem value="aggressive">Aggressive - Maximize efficiency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <LabelWithInfo info="If enabled, AI will automatically apply schedule optimizations. If disabled, AI will only suggest changes for human review and approval.">
                      AI Auto-Adjust Schedules
                    </LabelWithInfo>
                    <p className="text-sm text-muted-foreground">Allow AI to automatically adjust schedules (vs. suggest only)</p>
                  </div>
                  <Switch
                    checked={settings.scheduling_behavior.ai_auto_adjust_enabled}
                    onCheckedChange={(v) => updateNestedSetting("scheduling_behavior", "ai_auto_adjust_enabled", v)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <LabelWithInfo info="AI will analyze your schedule and provide recommendations for optimization, such as reordering tasks, adjusting durations, or identifying bottlenecks.">
                      AI Suggestions
                    </LabelWithInfo>
                    <p className="text-sm text-muted-foreground">Enable AI schedule optimization suggestions</p>
                  </div>
                  <Switch
                    checked={settings.scheduling_behavior.ai_suggestions_enabled}
                    onCheckedChange={(v) => updateNestedSetting("scheduling_behavior", "ai_suggestions_enabled", v)}
                  />
                </div>

                <div className="space-y-2">
                  <LabelWithInfo info="Extra buffer days added to anticipated (not yet confirmed) delivery dates to account for uncertainty. Helps prevent schedule slippage from late deliveries.">
                    Anticipated Delivery Buffer (days)
                  </LabelWithInfo>
                  <Input
                    type="number"
                    min="0"
                    value={settings.scheduling_behavior.anticipated_delivery_buffer_days}
                    onChange={(e) => updateNestedSetting("scheduling_behavior", "anticipated_delivery_buffer_days", parseInt(e.target.value) || 0)}
                    className="bg-muted/50 w-32"
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Constraint Enforcement</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <LabelWithInfo info="When enabled, a task cannot start until ALL predecessor tasks are 100% complete. When disabled, tasks may start with partial predecessor completion.">
                      Strict Task Dependencies
                    </LabelWithInfo>
                    <p className="text-sm text-muted-foreground">Enforce dependency relationships strictly</p>
                  </div>
                  <Switch
                    checked={settings.constraint_enforcement.task_dependencies_strict}
                    onCheckedChange={(v) => updateNestedSetting("constraint_enforcement", "task_dependencies_strict", v)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <LabelWithInfo info="When enabled, tasks requiring materials cannot be scheduled to start until the required materials have reached the 'available' state defined below.">
                      Material Delivery Blocks Task
                    </LabelWithInfo>
                    <p className="text-sm text-muted-foreground">Tasks cannot start until materials arrive</p>
                  </div>
                  <Switch
                    checked={settings.constraint_enforcement.material_delivery_blocks_task}
                    onCheckedChange={(v) => updateNestedSetting("constraint_enforcement", "material_delivery_blocks_task", v)}
                  />
                </div>

                <div className="space-y-2">
                  <LabelWithInfo info="Shipped: In transit from vendor. Delivered: Arrived at jobsite. Received: Signed for and documented. Inspected: Quality checked and approved. Staged: Positioned for installation.">
                    Delivery State for Availability
                  </LabelWithInfo>
                  <Select 
                    value={settings.constraint_enforcement.delivery_state_for_availability} 
                    onValueChange={(v) => updateNestedSetting("constraint_enforcement", "delivery_state_for_availability", v)}
                  >
                    <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="shipped">Shipped</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="received">Received</SelectItem>
                      <SelectItem value="inspected">Inspected</SelectItem>
                      <SelectItem value="staged">Staged</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">When materials are considered "available"</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Procurement & Cost */}
        <TabsContent value="procurement" className="space-y-6">
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-primary" />
                Procurement & Cost Settings
              </CardTitle>
              <CardDescription>Configure cost data sources and procurement handling</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Cost Database Source</Label>
                <Select 
                  value={settings.procurement_cost.cost_database_source} 
                  onValueChange={(v) => updateNestedSetting("procurement_cost", "cost_database_source", v)}
                >
                  <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual Entry</SelectItem>
                    <SelectItem value="rsmeans">RSMeans (Coming Soon)</SelectItem>
                    <SelectItem value="custom">Custom Integration</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Auto-Update Costs</Label>
                    <p className="text-sm text-muted-foreground">Automatically update from database</p>
                  </div>
                  <Switch
                    checked={settings.procurement_cost.costs_auto_update_enabled}
                    onCheckedChange={(v) => updateNestedSetting("procurement_cost", "costs_auto_update_enabled", v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Lock Costs Per Project</Label>
                    <p className="text-sm text-muted-foreground">Freeze costs once assigned</p>
                  </div>
                  <Switch
                    checked={settings.procurement_cost.costs_locked_per_project}
                    onCheckedChange={(v) => updateNestedSetting("procurement_cost", "costs_locked_per_project", v)}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Default Markup %</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={settings.procurement_cost.default_markup_percentage}
                    onChange={(e) => updateNestedSetting("procurement_cost", "default_markup_percentage", parseFloat(e.target.value) || 0)}
                    className="bg-muted/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Lead Time Buffer (days)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={settings.procurement_cost.lead_time_buffer_days}
                    onChange={(e) => updateNestedSetting("procurement_cost", "lead_time_buffer_days", parseInt(e.target.value) || 0)}
                    className="bg-muted/50"
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Vendor Management</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Enable Vendor Management</Label>
                    <p className="text-sm text-muted-foreground">Internal vendor tracking system</p>
                  </div>
                  <Switch
                    checked={settings.procurement_cost.vendor_management_enabled}
                    onCheckedChange={(v) => updateNestedSetting("procurement_cost", "vendor_management_enabled", v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Vendor Performance Tracking</Label>
                    <p className="text-sm text-muted-foreground">Track vendor delivery performance (Future)</p>
                  </div>
                  <Switch
                    checked={settings.procurement_cost.vendor_performance_tracking_enabled}
                    onCheckedChange={(v) => updateNestedSetting("procurement_cost", "vendor_performance_tracking_enabled", v)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Workforce & Resources */}
        <TabsContent value="workforce" className="space-y-6">
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Workforce & Resource Settings
              </CardTitle>
              <CardDescription>Configure workforce modeling and availability rules</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Workforce Model</Label>
                <Select 
                  value={settings.workforce_resource.workforce_model} 
                  onValueChange={(v) => updateNestedSetting("workforce_resource", "workforce_model", v)}
                >
                  <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individuals">Individuals Only</SelectItem>
                    <SelectItem value="crews">Crews Only</SelectItem>
                    <SelectItem value="both">Both Individuals & Crews</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Crews Reusable Across Projects</Label>
                    <p className="text-sm text-muted-foreground">Allow crews to work on multiple projects</p>
                  </div>
                  <Switch
                    checked={settings.workforce_resource.crews_reusable_across_projects}
                    onCheckedChange={(v) => updateNestedSetting("workforce_resource", "crews_reusable_across_projects", v)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Time Off Blocks Scheduling</Label>
                    <p className="text-sm text-muted-foreground">Personal time off prevents task assignment</p>
                  </div>
                  <Switch
                    checked={settings.workforce_resource.time_off_blocks_scheduling}
                    onCheckedChange={(v) => updateNestedSetting("workforce_resource", "time_off_blocks_scheduling", v)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Availability Enforcement</Label>
                  <Select 
                    value={settings.workforce_resource.availability_enforcement} 
                    onValueChange={(v) => updateNestedSetting("workforce_resource", "availability_enforcement", v)}
                  >
                    <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="strict">Strict - Block unavailable assignments</SelectItem>
                      <SelectItem value="advisory">Advisory - Warn but allow</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Compliance & Credentials</h4>
                <div className="space-y-2">
                  <Label>Credential Enforcement</Label>
                  <Select 
                    value={settings.workforce_resource.credential_enforcement} 
                    onValueChange={(v) => updateNestedSetting("workforce_resource", "credential_enforcement", v)}
                  >
                    <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="strict">Strict - Block missing credentials</SelectItem>
                      <SelectItem value="advisory">Advisory - Warn but allow</SelectItem>
                      <SelectItem value="disabled">Disabled - No enforcement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Missing Credentials Block Assignment</Label>
                    <p className="text-sm text-muted-foreground">Prevent assignment without required credentials</p>
                  </div>
                  <Switch
                    checked={settings.workforce_resource.missing_credentials_block_assignment}
                    onCheckedChange={(v) => updateNestedSetting("workforce_resource", "missing_credentials_block_assignment", v)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Automation & AI */}
        <TabsContent value="automation" className="space-y-6">
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-primary" />
                Automation & AI Settings
              </CardTitle>
              <CardDescription>Configure AI-powered automation features</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h4 className="font-medium">Auto-Generation Features</h4>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { key: "auto_generate_wbs", label: "WBS Structure" },
                    { key: "auto_generate_phases", label: "Phases" },
                    { key: "auto_generate_activities", label: "Activities" },
                    { key: "auto_generate_dependencies", label: "Dependencies" },
                    { key: "auto_generate_procurement_list", label: "Procurement List" },
                    { key: "auto_generate_cost_estimates", label: "Cost Estimates" },
                    { key: "auto_generate_schedule", label: "Schedule" },
                    { key: "auto_generate_resource_assignments", label: "Resource Assignments" },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <Label>{item.label}</Label>
                      <Switch
                        checked={settings.automation_ai[item.key]}
                        onCheckedChange={(v) => updateNestedSetting("automation_ai", item.key, v)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Review Requirements</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>AI Output Review Required</Label>
                    <p className="text-sm text-muted-foreground">AI-generated content must be reviewed before activation</p>
                  </div>
                  <Switch
                    checked={settings.automation_ai.ai_review_required}
                    onCheckedChange={(v) => updateNestedSetting("automation_ai", "ai_review_required", v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Scope Template Required</Label>
                    <p className="text-sm text-muted-foreground">Require structured scope format for AI</p>
                  </div>
                  <Switch
                    checked={settings.automation_ai.scope_template_required}
                    onCheckedChange={(v) => updateNestedSetting("automation_ai", "scope_template_required", v)}
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Approval Checkpoints</h4>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { key: "approval_checkpoint_budget", label: "Budget Finalization" },
                    { key: "approval_checkpoint_schedule_publish", label: "Schedule Publish" },
                    { key: "approval_checkpoint_procurement_orders", label: "Procurement Orders" },
                    { key: "approval_checkpoint_scope_changes", label: "Scope Changes" },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <Label>{item.label}</Label>
                      <Switch
                        checked={settings.automation_ai[item.key]}
                        onCheckedChange={(v) => updateNestedSetting("automation_ai", item.key, v)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Change Control */}
        <TabsContent value="changes" className="space-y-6">
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileEdit className="h-5 w-5 text-primary" />
                Change Control Settings
              </CardTitle>
              <CardDescription>Configure change request and approval workflows</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Formal Change Request Required</Label>
                    <p className="text-sm text-muted-foreground">Require formal change requests for modifications</p>
                  </div>
                  <Switch
                    checked={settings.change_control.formal_change_request_required}
                    onCheckedChange={(v) => updateNestedSetting("change_control", "formal_change_request_required", v)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Auto-Apply Changes</Label>
                    <p className="text-sm text-muted-foreground">Apply changes automatically (vs. requiring approval)</p>
                  </div>
                  <Switch
                    checked={settings.change_control.auto_apply_changes}
                    onCheckedChange={(v) => updateNestedSetting("change_control", "auto_apply_changes", v)}
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Approval Thresholds</h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Cost Delta Threshold (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={settings.change_control.cost_delta_threshold_percentage}
                      onChange={(e) => updateNestedSetting("change_control", "cost_delta_threshold_percentage", parseFloat(e.target.value) || 0)}
                      className="bg-muted/50"
                    />
                    <p className="text-sm text-muted-foreground">Changes exceeding this % require approval</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Schedule Delta Threshold (days)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={settings.change_control.schedule_delta_threshold_days}
                      onChange={(e) => updateNestedSetting("change_control", "schedule_delta_threshold_days", parseInt(e.target.value) || 0)}
                      className="bg-muted/50"
                    />
                    <p className="text-sm text-muted-foreground">Changes exceeding this many days require approval</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications" className="space-y-6">
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-primary" />
                Notification Settings
              </CardTitle>
              <CardDescription>Configure notification channels and triggers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h4 className="font-medium">Channels</h4>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <Label>Email</Label>
                    <Switch
                      checked={settings.notifications.channels.email_enabled}
                      onCheckedChange={(v) => updateDeepNestedSetting("notifications", "channels", "email_enabled", v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <Label>SMS</Label>
                    <Switch
                      checked={settings.notifications.channels.sms_enabled}
                      onCheckedChange={(v) => updateDeepNestedSetting("notifications", "channels", "sms_enabled", v)}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <Label>In-App</Label>
                    <Switch
                      checked={settings.notifications.channels.in_app_enabled}
                      onCheckedChange={(v) => updateDeepNestedSetting("notifications", "channels", "in_app_enabled", v)}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Event Triggers</h4>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { key: "notify_on_project_created", label: "Project Created" },
                    { key: "notify_on_task_assigned", label: "Task Assigned" },
                    { key: "notify_on_task_completed", label: "Task Completed" },
                    { key: "notify_on_approval_requested", label: "Approval Requested" },
                    { key: "notify_on_approval_decided", label: "Approval Decided" },
                    { key: "notify_on_schedule_change", label: "Schedule Change" },
                    { key: "notify_on_budget_change", label: "Budget Change" },
                    { key: "notify_on_delivery_update", label: "Delivery Update" },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <Label>{item.label}</Label>
                      <Switch
                        checked={settings.notifications[item.key]}
                        onCheckedChange={(v) => updateNestedSetting("notifications", item.key, v)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notification Frequency</Label>
                <Select 
                  value={settings.notifications.notification_frequency} 
                  onValueChange={(v) => updateNestedSetting("notifications", "notification_frequency", v)}
                >
                  <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="realtime">Real-time</SelectItem>
                    <SelectItem value="daily_digest">Daily Digest</SelectItem>
                    <SelectItem value="weekly_digest">Weekly Digest</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">AI-Generated Messaging</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>AI-Generated Messages</Label>
                    <p className="text-sm text-muted-foreground">Use AI to generate notification messages</p>
                  </div>
                  <Switch
                    checked={settings.notifications.ai_generated_messages_enabled}
                    onCheckedChange={(v) => updateNestedSetting("notifications", "ai_generated_messages_enabled", v)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Message Tone</Label>
                  <Select 
                    value={settings.notifications.ai_message_tone} 
                    onValueChange={(v) => updateNestedSetting("notifications", "ai_message_tone", v)}
                  >
                    <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="friendly">Friendly</SelectItem>
                      <SelectItem value="brief">Brief</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">External Stakeholders</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>External Notifications</Label>
                    <p className="text-sm text-muted-foreground">Send updates to clients/subcontractors</p>
                  </div>
                  <Switch
                    checked={settings.notifications.external_stakeholder_notifications_enabled}
                    onCheckedChange={(v) => updateNestedSetting("notifications", "external_stakeholder_notifications_enabled", v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Require Approval for External</Label>
                    <p className="text-sm text-muted-foreground">External notifications need approval</p>
                  </div>
                  <Switch
                    checked={settings.notifications.external_notification_approval_required}
                    onCheckedChange={(v) => updateNestedSetting("notifications", "external_notification_approval_required", v)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Client Update Frequency</Label>
                  <Select 
                    value={settings.notifications.client_update_frequency} 
                    onValueChange={(v) => updateNestedSetting("notifications", "client_update_frequency", v)}
                  >
                    <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="milestone">At Milestones</SelectItem>
                      <SelectItem value="manual">Manual Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Access & Visibility */}
        <TabsContent value="access" className="space-y-6">
          <Card className="stat-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Access & Visibility Settings
              </CardTitle>
              <CardDescription>Configure role permissions and data visibility</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h4 className="font-medium">Data Visibility by Role</h4>
                <div className="space-y-2">
                  <Label>Roles with Cost Data Access</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Current: {settings.visibility_access.cost_data_visibility_roles?.join(", ") || "None"}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Roles with Schedule Access</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Current: {settings.visibility_access.schedule_visibility_roles?.join(", ") || "None"}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Roles with Procurement Access</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Current: {settings.visibility_access.procurement_visibility_roles?.join(", ") || "None"}
                  </p>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="font-medium">Dashboard Configuration</h4>
                <div className="space-y-2">
                  <Label>Default View</Label>
                  <Select 
                    value={settings.dashboard_config.default_view} 
                    onValueChange={(v) => updateNestedSetting("dashboard_config", "default_view", v)}
                  >
                    <SelectTrigger className="bg-muted/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portfolio">Portfolio Overview</SelectItem>
                      <SelectItem value="project">Project Detail</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { key: "show_budget_metrics", label: "Show Budget Metrics" },
                    { key: "show_schedule_metrics", label: "Show Schedule Metrics" },
                    { key: "show_procurement_metrics", label: "Show Procurement Metrics" },
                    { key: "show_workforce_metrics", label: "Show Workforce Metrics" },
                    { key: "portfolio_rollups_enabled", label: "Portfolio Rollups" },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <Label>{item.label}</Label>
                      <Switch
                        checked={settings.dashboard_config[item.key]}
                        onCheckedChange={(v) => updateNestedSetting("dashboard_config", item.key, v)}
                      />
                    </div>
                  ))}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>On-Budget Threshold (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={settings.dashboard_config.on_budget_threshold_percentage}
                      onChange={(e) => updateNestedSetting("dashboard_config", "on_budget_threshold_percentage", parseFloat(e.target.value) || 0)}
                      className="bg-muted/50"
                    />
                    <p className="text-sm text-muted-foreground">Within this % is "on-budget"</p>
                  </div>
                  <div className="space-y-2">
                    <Label>On-Schedule Threshold (days)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={settings.dashboard_config.on_schedule_threshold_days}
                      onChange={(e) => updateNestedSetting("dashboard_config", "on_schedule_threshold_days", parseInt(e.target.value) || 0)}
                      className="bg-muted/50"
                    />
                    <p className="text-sm text-muted-foreground">Within this many days is "on-schedule"</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
